import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Stack,
  Typography,
  Checkbox,
  Paper,
} from "@mui/material";

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("de-DE");
};

const buildParticipantEntry = (member = {}) => ({
  _key: `member-${member._id}`,
  memberRef: member._id,
  firstName: member.firstName || "",
  lastName: member.lastName || "",
  birthdate: member.birthdate || undefined,
  photoOptOut: false,
});

export default function BookingStepParticipants({
  eligibleMembers,
  participants,
  onChange,
}) {
  const normalizedMembers = useMemo(() => {
    const source = Array.isArray(eligibleMembers) ? eligibleMembers : [];
    return source
      .map((member) => ({
        _id: String(member?._id || "").trim(),
        firstName: String(member?.firstName || "").trim(),
        lastName: String(member?.lastName || "").trim(),
        birthdate: member?.birthdate || undefined,
        memberNumber: String(member?.memberNumber || "").trim(),
      }))
      .filter((member) => Boolean(member._id))
      .sort((left, right) =>
        `${left.lastName} ${left.firstName}`.localeCompare(
          `${right.lastName} ${right.firstName}`,
          "de",
        ),
      );
  }, [eligibleMembers]);

  const selectedMemberIds = new Set(
    (Array.isArray(participants) ? participants : [])
      .map((participant) => String(participant?.memberRef || "").trim())
      .filter(Boolean),
  );

  const toggleMember = (memberId) => {
    const nextSelected = new Set(selectedMemberIds);
    if (nextSelected.has(memberId)) {
      nextSelected.delete(memberId);
    } else {
      nextSelected.add(memberId);
    }

    const nextParticipants = normalizedMembers
      .filter((member) => nextSelected.has(member._id))
      .map(buildParticipantEntry);

    onChange(nextParticipants);
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Welche Mitglieder nehmen teil?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Bitte wählen Sie die Vertragsmitglieder aus, die eingebucht werden
        sollen.
      </Typography>

      {normalizedMembers.length === 0 ? (
        <Alert severity="warning">
          Es wurden keine buchbaren Vertragsmitglieder gefunden.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {normalizedMembers.map((member) => {
            const displayName =
              `${member.firstName} ${member.lastName}`.trim() || "Mitglied";
            const metaParts = [];
            if (member.birthdate)
              metaParts.push(`geb. ${formatDate(member.birthdate)}`);
            if (member.memberNumber)
              metaParts.push(`Nr. ${member.memberNumber}`);
            const meta = metaParts.join(" - ");

            return (
              <Paper
                key={member._id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "border-color 0.15s, background-color 0.15s",
                  borderColor: selectedMemberIds.has(member._id)
                    ? "primary.main"
                    : "divider",
                  bgcolor: selectedMemberIds.has(member._id)
                    ? "rgba(25, 118, 210, 0.04)"
                    : "background.paper",
                  "&:hover": {
                    borderColor: "primary.light",
                    bgcolor: "rgba(25, 118, 210, 0.06)",
                  },
                }}
                onClick={() => toggleMember(member._id)}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={selectedMemberIds.has(member._id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleMember(member._id)}
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {displayName}
                    </Typography>
                    {meta ? (
                      <Typography variant="caption" color="text.secondary">
                        {meta}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {participants.length === 0 ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          Bitte mindestens ein Mitglied auswählen.
        </Typography>
      ) : null}
    </Box>
  );
}

BookingStepParticipants.propTypes = {
  eligibleMembers: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      birthdate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
      ]),
      memberNumber: PropTypes.string,
    }),
  ),
  participants: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

BookingStepParticipants.defaultProps = {
  eligibleMembers: [],
};
